import { useCallback, useState, type ComponentProps, type MouseEvent } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'

import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import type TripListView from './TripListView'
import TripListCheckboxButton from './TripListCheckboxButton'
import TripListMobileRowActionsMenu from './TripListMobileRowActionsMenu'
import TripListRowNote from './TripListRowNote'
import TripListRowPrice from './TripListRowPrice'
import TripListRowStatus from './TripListRowStatus'
import TripListTypeIcon from './TripListTypeIcon'
import type { TripListRow, TripListStatusValue } from './tripListTypes'

type TripListViewProps = ComponentProps<typeof TripListView>

interface Props {
  row: TripListRow
  tripStops: TripListViewProps['tripStops']
  selected: boolean
  selectionMode: boolean
  luxPlusEnabled: boolean
  isBookingPending: boolean
  isNoteUpdatePending: boolean
  isStatusUpdatePending: boolean
  notesDisabled: boolean
  statusDisabled: boolean
  actionsDisabled: boolean
  onSelectRow: TripListViewProps['onSelectRow']
  onRowClick: TripListViewProps['onRowClick']
  onBookRow: TripListViewProps['onBookRow']
  onViewBooking: TripListViewProps['onViewBooking']
  onAskAi: TripListViewProps['onAskAi']
  onAskAiMove: TripListViewProps['onAskAiMove']
  onDelete: TripListViewProps['onDelete']
  onMoveToDay: TripListViewProps['onMoveToDay']
  onUpdateNote: TripListViewProps['onUpdateNote']
  onUpdateStatus: TripListViewProps['onUpdateStatus']
}

const Card = styled.article`
  display: grid;
  grid-template-columns: ${rem(24)} ${rem(44)} minmax(0, 1fr) ${rem(36)};
  gap: ${rem(10)};
  align-items: start;
  padding-block: ${rem(12)};
  padding-inline: ${rem(16)};
  border-block-end: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};

  &.is-selected {
    background: ${props => `color-mix(in srgb, ${props.theme.palette.highlight.primary.lightBackground}, ${props.theme.palette.neutral.surface.normalBackground} 64%)`};
  }

  &.is-clickable {
    cursor: pointer;
  }

  &.is-selection-mode > *:not(.is-selection-control) {
    pointer-events: none;
  }
`

const RowHandleSlot = styled.div`
  position: relative;
  display: flex;
  min-height: ${rem(44)};
  align-items: center;
  justify-content: center;
`

const DragHandle = styled.span`
  width: ${rem(12)};
  height: ${rem(20)};
  transform: translate(${rem(6)}, ${rem(-8)});
  background-image:
    radial-gradient(circle, ${props => props.theme.palette.neutral.surface.lightForegroundAlt} ${rem(2)}, transparent ${rem(2)}),
    radial-gradient(circle, ${props => props.theme.palette.neutral.surface.lightForegroundAlt} ${rem(2)}, transparent ${rem(2)});
  background-size: ${rem(7)} ${rem(7)};
  background-position: 0 0, ${rem(6)} 0;
  background-repeat: repeat-y;
`

const SelectionCheckbox = styled(TripListCheckboxButton)`
  &.size-small {
    width: ${rem(18)};
    height: ${rem(18)};
    transform: translate(${rem(6)}, ${rem(-8)});
  }

  input {
    border-radius: ${rem(4)};
  }

  input:not(:checked):not(:indeterminate) {
    border-width: ${rem(2)};
    border-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }

  input:not(:checked):not(:indeterminate):hover {
    border-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }
`

const IconSlot = styled.div`
  display: flex;
  justify-content: center;
  padding-block-start: ${rem(2)};
`

const CardTitleStack = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: ${rem(2)};
`

const CardMetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(8)};
  flex-wrap: nowrap;
  margin-block-start: ${rem(4)};
`

const PriceSlot = styled.div`
  display: flex;
  min-width: 0;
  margin-inline-start: auto;
  justify-content: flex-end;
`

function TripListMobileRowCard({
  row,
  tripStops,
  selected,
  selectionMode,
  luxPlusEnabled,
  isBookingPending,
  isNoteUpdatePending,
  isStatusUpdatePending,
  notesDisabled,
  statusDisabled,
  actionsDisabled,
  onSelectRow,
  onRowClick,
  onBookRow,
  onViewBooking,
  onAskAi,
  onAskAiMove,
  onDelete,
  onMoveToDay,
  onUpdateNote,
  onUpdateStatus,
}: Props) {
  const intl = useIntl()
  const [noteEditing, setNoteEditing] = useState(false)
  const canView = row.viewTarget.kind !== 'none'

  const selectLabel = intl.formatMessage(
    {
      id: 'uEW07qIriZwmUSm6ljqV',
      defaultMessage: 'Select {title}',
      description: 'Accessible label for selecting a row in the mobile TripChat itinerary list view',
    },
    { title: row.title },
  )
  const handleSelectChange = useCallback(() => {
    onSelectRow(row.id, !selected)
  }, [onSelectRow, row.id, selected])

  const handleControlClick = useCallback((event: MouseEvent) => {
    if (selectionMode) return
    event.stopPropagation()
  }, [selectionMode])

  const handleCheckboxClick = useCallback((event: MouseEvent) => {
    event.stopPropagation()
  }, [])

  const handleViewClick = useCallback(() => {
    if (actionsDisabled || !canView) return
    onRowClick(row)
  }, [actionsDisabled, canView, onRowClick, row])

  const handleEditNoteClick = useCallback(() => {
    if (notesDisabled) return
    setNoteEditing(true)
  }, [notesDisabled])

  const handleNoteChange = useCallback((notes: string | undefined) => {
    if (notesDisabled || isNoteUpdatePending) return
    onUpdateNote(row, notes)
  }, [isNoteUpdatePending, notesDisabled, onUpdateNote, row])

  const handleStatusChange = useCallback((status: TripListStatusValue) => {
    if (statusDisabled || isStatusUpdatePending) return
    onUpdateStatus(row, status)
  }, [isStatusUpdatePending, onUpdateStatus, row, statusDisabled])

  const handleCardClick = useCallback(() => {
    if (selectionMode) {
      handleSelectChange()
      return
    }
    if (canView) {
      handleViewClick()
    }
  }, [canView, handleSelectChange, handleViewClick, selectionMode])

  const isCardClickable = selectionMode || canView

  return (
    <Card
      onClick={isCardClickable ? handleCardClick : undefined}
      className={clsx({
        'is-selected': selected,
        'is-clickable': isCardClickable,
        'is-selection-mode': selectionMode,
      })}
      data-testid={`trip-list-mobile-row-${row.id}`}
      data-stop-id={row.stop.id}
      data-day-id={row.day?.id}
      data-item-id={row.kind === 'item' ? row.item.id : row.option.itemId}
    >
      <RowHandleSlot
        className={selectionMode ? 'is-selection-control' : undefined}
        onClick={handleControlClick}
      >
        {selectionMode && (
          <SelectionCheckbox
            size="small"
            checked={selected}
            onClick={handleCheckboxClick}
            onChange={handleSelectChange}
            aria-label={selectLabel}
          />
        )}
        {!selectionMode && <DragHandle aria-hidden="true" />}
      </RowHandleSlot>
      <IconSlot>
        <TripListTypeIcon type={row.rowType} row={row} />
      </IconSlot>
      <CardTitleStack>
        <BodyText variant="medium" weight="bold" lineClamp={2}>
          {row.title}
        </BodyText>
        {row.subtitle && (
          <Caption variant="large" colour="light-foreground-alt" lineClamp={2}>
            {row.subtitle}
          </Caption>
        )}
        {(row.note || noteEditing) && (
          <TripListRowNote
            note={row.note}
            title={row.title}
            disabled={notesDisabled || selectionMode}
            saving={isNoteUpdatePending}
            editing={noteEditing}
            onEditingChange={setNoteEditing}
            onChange={handleNoteChange}
          />
        )}
        <CardMetaRow onClick={handleControlClick}>
          <TripListRowStatus
            status={row.status}
            title={row.title}
            disabled={selectionMode || statusDisabled}
            saving={isStatusUpdatePending}
            onChange={handleStatusChange}
          />
          <PriceSlot>
            <TripListRowPrice price={row.price} luxPlusEnabled={luxPlusEnabled} compact />
          </PriceSlot>
        </CardMetaRow>
      </CardTitleStack>
      {!selectionMode && (
        <TripListMobileRowActionsMenu
          row={row}
          tripStops={tripStops}
          isBookingPending={isBookingPending}
          notesDisabled={notesDisabled}
          actionsDisabled={actionsDisabled}
          onViewRow={handleViewClick}
          onBookRow={onBookRow}
          onViewBooking={onViewBooking}
          onAskAi={onAskAi}
          onAskAiMove={onAskAiMove}
          onDelete={onDelete}
          onMoveToDay={onMoveToDay}
          onEditNote={handleEditNoteClick}
        />
      )}
    </Card>
  )
}

export default TripListMobileRowCard
